if (!customElements.get('experience-upsell-recommendations')) {
  customElements.define(
    'experience-upsell-recommendations',
    class ExperienceUpsellRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.placeInProductInfo()) return;

        this.handleToggle = this.handleToggle.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleKeyboardAddToCart = this.handleKeyboardAddToCart.bind(this);
        this.addEventListener('click', this.handleToggle);
        this.addEventListener('change', this.handleVariantChange);
        this.addEventListener('keydown', this.handleKeyboardAddToCart);

        if (this.dataset.loaded === 'true') return;
        if (!this.dataset.url) return;
        this.loadRecommendations();
      }

      disconnectedCallback() {
        if (this.requestController) this.requestController.abort();
        if (this.requestTimeout) window.clearTimeout(this.requestTimeout);
        this.removeEventListener('click', this.handleToggle);
        this.removeEventListener('change', this.handleVariantChange);
        this.removeEventListener('keydown', this.handleKeyboardAddToCart);
      }

      async loadRecommendations() {
        if (this.loading) return;
        this.loading = true;
        this.requestController = new AbortController();
        this.requestTimeout = window.setTimeout(() => this.requestController.abort(), 8000);

        try {
          const response = await fetch(this.dataset.url, { signal: this.requestController.signal });
          if (!response.ok) throw new Error(`Upsell recommendations request failed: ${response.status}`);

          const text = await response.text();
          const html = new DOMParser().parseFromString(text, 'text/html');
          const recommendations = html.querySelector('experience-upsell-recommendations');

          if (!recommendations || recommendations.dataset.loaded !== 'true' || !recommendations.innerHTML.trim()) {
            this.hidden = true;
            return;
          }

          this.innerHTML = recommendations.innerHTML;
          this.dataset.loaded = 'true';
          this.setAttribute('aria-busy', 'false');
        } catch (error) {
          this.hidden = true;
          if (error.name !== 'AbortError') console.error(error);
        } finally {
          window.clearTimeout(this.requestTimeout);
          this.loading = false;
        }
      }

      placeInProductInfo() {
        const source = this.closest('[data-experience-upsell-section]');
        if (!source || source.dataset.placed === 'true') return false;

        const productInfo = document.querySelector('.product__info-container, [id^="ProductInfo-"]');
        if (!productInfo) return false;

        const directChildOfProductInfo = (element) => {
          let block = element;

          while (block && block.parentElement !== productInfo) {
            block = block.parentElement;
          }

          return block?.parentElement === productInfo ? block : null;
        };

        const description = productInfo.querySelector('.product__description, [itemprop="description"]');
        const descriptionBlock = directChildOfProductInfo(description);

        if (descriptionBlock) {
          source.dataset.placed = 'true';
          source.hidden = false;
          descriptionBlock.before(source);
          return true;
        }

        const commerceElements = [
          productInfo.querySelector('[id^="ProductBuyButtons-"]'),
          productInfo.querySelector('[id^="price-"]'),
          productInfo.querySelector('variant-selects, variant-radios, [id^="variant-selects"]'),
        ];

        const commerceBlocks = commerceElements
          .map(directChildOfProductInfo)
          .filter(Boolean)
          .sort(
            (first, second) =>
              Array.from(productInfo.children).indexOf(first) - Array.from(productInfo.children).indexOf(second)
          );
        const lastCommerceBlock = commerceBlocks.at(-1);
        if (!lastCommerceBlock) return false;

        source.dataset.placed = 'true';
        source.hidden = false;
        lastCommerceBlock.after(source);
        return true;
      }

      handleVariantChange(event) {
        const select = event.target.closest('[data-upsell-variant]');
        if (!select || !this.contains(select)) return;

        const option = select.options[select.selectedIndex];
        const item = select.closest('.experience-upsell__item');
        const image = item?.querySelector('.experience-upsell__image');
        const imageSrc = option?.dataset.imageSrc;
        if (!image || !imageSrc || image.dataset.variantImage === imageSrc) return;

        const selectedVariantId = select.value;
        const preloadedImage = new Image();
        const applyImage = () => {
          if (select.value !== selectedVariantId) return;

          image.src = imageSrc;
          if (option.dataset.imageSrcset) image.srcset = option.dataset.imageSrcset;
          if (option.dataset.imageAlt) image.alt = option.dataset.imageAlt;
          image.dataset.variantImage = imageSrc;
        };

        preloadedImage.onload = applyImage;
        preloadedImage.src = imageSrc;
        if (preloadedImage.complete) applyImage();
      }

      handleToggle(event) {
        const trigger = event.target.closest('[data-upsell-toggle]');
        if (!trigger || !this.contains(trigger)) return;

        const item = trigger.closest('.experience-upsell__item');
        const detailsId = trigger.getAttribute('aria-controls');
        const details = item?.querySelector(`[id="${detailsId}"]`);
        if (!item || !details) return;

        const shouldOpen = details.hidden;
        details.hidden = !shouldOpen;
        item.classList.toggle('is-expanded', shouldOpen);

        item.querySelectorAll(`[aria-controls="${detailsId}"]`).forEach((button) => {
          button.setAttribute('aria-expanded', String(shouldOpen));
        });
      }

      handleKeyboardAddToCart(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        const addToCart = event.target.closest('add-to-cart');
        if (!addToCart) return;

        event.preventDefault();
        addToCart.click();
      }
    }
  );
}
